import React, { useState, useEffect, useRef } from 'react';
import nbrApi from '../../../services/nbrApi';
import moment from 'moment';
import Chart from 'react-apexcharts';

const RoomStatusSnapshots = ({ properties_id }) => {
    const [dateStr, setDateStr] = useState(moment().subtract(1, 'days').format('YYYY-MM-DD'));
    const startOfDay = moment(dateStr + ' 00:00:00').valueOf();
    const endOfDay = moment(dateStr + ' 23:59:59').valueOf();
    const [snapshotDates, setSnapshotDates] = useState([]);
    const [selectedDate, setSelectedDate] = useState("");
    const chartRef = useRef(null);
    const STORAGE_KEY = 'roomStatusHistory_seriesVisibility';
  
    const [chartData, setChartData] = useState({
        series: [],
        options: {
            chart: {
                type: 'line',
                height: 450,
                zoom: {
                    enabled: false
                },
                toolbar: {
                    show: false
                }
            },
            title: {
                align: 'center',
                style: {
                    fontSize: '20px',
                    color: '#333'
                }
            },
            xaxis: {
                type: "datetime",
                min: startOfDay,
                max: endOfDay,
                tickAmount: 24, // Show 24 ticks for each hour
                axisTicks: {
                    show: true
                },
                axisBorder: {
                    show: true
                },
                title: {
                    text: 'Time',
                    style: {
                        color: '#333'
                    }
                },
                labels: {
                    style: {
                        colors: '#333',
                        fontSize: '10px'
                    },
                    formatter: function (value) {
                        return moment(value).format('HH:mm');
                    },
                    datetimeUTC: false
                }
            },
            yaxis: {
                title: {
                    text: 'Room Count',
                    style: {
                        color: '#333'
                    }
                },
                labels: {
                    style: {
                        colors: '#333'
                    }
                },
                min: 0
            },
            stroke: {
                curve: 'smooth',
                width: 2,
                colors: ['#1f77b4', '#2ca02c', '#ff7f0e', '#d62728', '#9467bd',
                    '#17becf', '#bcbd22', '#e377c2', '#7f7f7f', '#8c564b', '#3366cc']
            },
            // fill: {
            //     type: 'gradient',
            //     gradient: {
            //         shade: 'light',
            //         type: 'horizontal',
            //         shadeIntensity: 0.5,
            //         gradientToColors: undefined,
            //         inverseColors: true,
            //         opacityFrom: 0.7,
            //         opacityTo: 0.9,
            //         stops: [0, 50, 100],
            //         colorStops: []
            //     }
            // },
            tooltip: {
                enabled: true,
                theme: 'dark',
                x: {
                    format: 'HH:mm'
                }
            },
            legend: {
                position: 'top',
                horizontalAlign: 'right',
                labels: {
                    colors: '#333'
                }
            },
            grid: {
                borderColor: '#e7e7e7',
                xaxis: {
                    lines: {
                        show: true
                    }
                },
                yaxis: {
                    lines: {
                        show: true
                    }
                }
            },
            dataLabels: {
                enabled: false
            }
        }
    });

    let isFirstFetch = useRef(true); // Flag to track the first fetch
    const isUserInteraction = useRef(false); // Flag to track if user is interacting with legend
    const visibilityApplied = useRef(false); // Flag to track if visibility has been applied

    // Load saved series visibility state from localStorage
    const loadSeriesVisibility = () => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
                return JSON.parse(saved);
            }
        } catch (error) {
            console.error("Error loading series visibility:", error);
        }
        return {}; // Return empty object if nothing saved or error
    };

    // Save series visibility state to localStorage
    const saveSeriesVisibility = (visibilityState) => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(visibilityState));
        } catch (error) {
            console.error("Error saving series visibility:", error);
        }
    };

    // Apply saved visibility state to chart
    const applySeriesVisibility = (chartContext, seriesVisibility) => {
        if (!chartContext || !seriesVisibility) return;
        
        // chartContext has the chart instance directly or via chart property
        const chart = chartContext.chart || chartContext;
        if (!chart || typeof chart.hideSeries !== 'function') return;
        
        Object.keys(seriesVisibility).forEach((seriesName) => {
            const isVisible = seriesVisibility[seriesName];
            if (!isVisible) {
                // Hide the series if it should be hidden
                chart.hideSeries(seriesName);
            } else {
                // Show the series if it should be visible
                chart.showSeries(seriesName);
            }
        });
    };

    useEffect(() => {
        const fetchSnapshotDates = async () => {
            try {
                const response = await nbrApi.post('/getsnapshotdatelist', { properties_id });
                // Response contains objects with snapshot_date property
                setSnapshotDates(response.data);

                // Only execute the following block on the first fetch
                if (isFirstFetch.current && response.data && response.data.length > 0) {
                    const firstDate = response.data[0];
                    // Ensure we extract snapshot_date as a string
                    const firstSnapshotDate = typeof firstDate === 'string' 
                        ? firstDate 
                        : (firstDate?.snapshot_date || firstDate);
                    setSelectedDate(firstSnapshotDate);
                    fetchRoomStatusSnapshots(firstSnapshotDate);
                    setDateStr(moment(new Date(firstSnapshotDate)).format('YYYY-MM-DD'));
                    isFirstFetch.current = false; // Mark the first fetch as complete
                }
            } catch (error) {
                console.error("Error fetching snapshot dates:", error);
            }
        };

        const fetchOnFocus = async () => {
            if (selectedDate && document.hasFocus()) {
                await fetchRoomStatusSnapshots(selectedDate);
            }
        };

        // Fetch snapshot dates immediately when the component mounts
        fetchSnapshotDates();

        // Set up a 1-minute interval to fetch data if the window is focused
        const interval = setInterval(() => {
            fetchOnFocus();
        }, 60000); // Fetch every 1 minute

        // Set up a focus event listener to fetch data when the window regains focus
        const handleFocus = () => {
            fetchOnFocus();
        };

        window.addEventListener("focus", handleFocus);

        return () => {
            clearInterval(interval); // Clear interval on component unmount
            window.removeEventListener("focus", handleFocus); // Remove focus event listener
        };
    }, [properties_id, selectedDate]);

    const fetchRoomStatusSnapshots = async (snapshot_date) => {
        try {
            // Ensure snapshot_date is a string, not an object
            const snapshotDateString = typeof snapshot_date === 'string' 
                ? snapshot_date 
                : (snapshot_date?.snapshot_date || snapshot_date);
            
            const response = await nbrApi.post('/getroomstatussnapshots', {
                properties_id,
                snapshot_date: snapshotDateString
            });

            const data = response.data;
            const statusFields = [
                'checkout_clean_complete',
                'checkout_clean_in_process',
                'do_not_disturb',
                'guest_checked_in_today',
                'inspection_in_process',
                'pending_checkout',
                'ready_to_occupy',
                'stayover_clean_complete',
                'stayover_clean_in_process',
                'stayover_dirty',
                'vacant_dirty'
            ];

            // Series styling configuration
            const seriesConfig = {
                'ready_to_occupy': {
                    color: '#33ccff',
                    width: 4, // Thicker line
                    dashArray: 0 // Solid line
                },
                'stayover_clean_complete': {
                    color: '#99e6ff',
                    width: 2,
                    dashArray: 0
                },
                'stayover_dirty': {
                    color: '#bfbfbf',
                    width: 4, // Thicker line
                    dashArray: 0
                },
                'vacant_dirty': {
                    color: '#666666',
                    width: 4, // Thicker line
                    dashArray: 0
                },
                'pending_checkout': {
                    color: '#ffd480',
                    width: 2,
                    dashArray: 5 // Dashed line
                },
                'do_not_disturb': {
                    color: '#cc2900',
                    width: 2,
                    dashArray: 5 // Dashed line
                },
                'guest_checked_in_today': {
                    color: '#ffd480',
                    width: 2,
                    dashArray: 0
                },
                'checkout_clean_complete': {
                    color: '#d9b3ff',
                    width: 2,
                    dashArray: 5 // Dashed line
                },
                'checkout_clean_in_process': {
                    color: '#1f77b4',
                    width: 2,
                    dashArray: 0
                },
                'inspection_in_process': {
                    color: '#2ca02c',
                    width: 2,
                    dashArray: 0
                },
                'stayover_clean_in_process': {
                    color: '#ff7f0e',
                    width: 2,
                    dashArray: 0
                }
            };

            // Sort and process data as before
            const sortedData = data.sort((a, b) => new Date(a.local_time) - new Date(b.local_time));
            
            if (!sortedData || sortedData.length === 0) {
                return;
            }

            const dateStr = sortedData[0]?.local_time.split(' ')[0];
            const startOfDay = moment(sortedData[0].local_time).startOf('day').valueOf();
            
            // Get the first and last timestamps from actual data
            const firstTimestamp = moment(sortedData[0].local_time).valueOf();
            const lastTimestamp = moment(sortedData[sortedData.length - 1].local_time).valueOf();
            
            // Calculate endOfDay based on the LAST data point to ensure all data is visible
            // Add 1 hour buffer to ensure the latest data point is fully visible
            const endOfDay = Math.max(
                moment(sortedData[sortedData.length - 1].local_time).endOf('day').valueOf(),
                lastTimestamp + (60 * 60 * 1000) // Add 1 hour buffer
            );

            const seriesData = {};
            statusFields.forEach(status => {
                seriesData[status] = [];
                // Start from the first data point, not start of day
                seriesData[status].push({ x: firstTimestamp, y: 0 });
            });

            sortedData.forEach(snapshot => {
                const timestamp = moment(snapshot.local_time).valueOf();
                statusFields.forEach(field => {
                    seriesData[field].push({
                        x: timestamp,
                        y: parseInt(snapshot[field]) || 0
                    });
                });
            });

            // Calculate the time range based on actual data
            const dataTimeRange = lastTimestamp - firstTimestamp;
            const hoursInRange = dataTimeRange / (1000 * 60 * 60); // Convert to hours
            // Calculate appropriate tick amount based on data range (max 24 ticks)
            const tickAmount = Math.min(Math.max(Math.ceil(hoursInRange), 2), 24);

            // Build arrays for stroke configuration matching series order
            const strokeWidths = statusFields.map(status => {
                const config = seriesConfig[status] || { width: 2 };
                return config.width;
            });

            const dashArrays = statusFields.map(status => {
                const config = seriesConfig[status] || { dashArray: 0 };
                return config.dashArray;
            });

            const strokeColors = statusFields.map(status => {
                const config = seriesConfig[status] || { color: '#1f77b4' };
                return config.color;
            });

            // Build series array maintaining the order from statusFields
            const series = statusFields.map(status => {
                const config = seriesConfig[status] || {
                    color: '#1f77b4',
                    width: 2,
                    dashArray: 0
                };
                return {
                    name: status.replace(/_/g, ' ').toUpperCase(),
                    data: seriesData[status] || [],
                    color: config.color
                };
            });

            // Reset visibility applied flag when new data is loaded
            visibilityApplied.current = false;

            setChartData(prev => ({
                ...prev,
                series,
                options: {
                    ...prev.options,
                    stroke: {
                        curve: 'smooth',
                        width: strokeWidths, // Array of widths, one per series
                        colors: strokeColors,
                        dashArray: dashArrays
                    },
                    xaxis: {
                        ...prev.options.xaxis,
                        min: startOfDay,
                        max: endOfDay,
                        tickAmount: 24, // Show 24 ticks for each hour
                        labels: {
                            ...prev.options.xaxis.labels,
                            formatter: function(value) {
                                const m = moment(value);
                                // Format as HH:00 to show top of each hour
                                const hour = m.hour();
                                return String(hour).padStart(2, '0') + ':00';
                            },
                            datetimeUTC: false
                        }
                    },
                    legend: {
                        ...prev.options.legend,
                        position: 'top',
                        horizontalAlign: 'right',
                        labels: {
                            colors: '#333'
                        },
                        markers: {
                            width: 12,
                            height: 12,
                            strokeWidth: 0,
                            strokeColor: '#fff',
                            fillColors: strokeColors,
                            radius: 12,
                            customHTML: undefined
                        }
                    },
                    chart: {
                        ...prev.options.chart,
                        events: {
                            legendClick: function(chartContext, seriesIndex, config) {
                                // Mark that this is a user interaction
                                isUserInteraction.current = true;
                                
                                // Get the series name
                                const seriesName = chartContext.w.globals.seriesNames[seriesIndex];
                                
                                // After legend click, ApexCharts automatically toggles the series
                                // We need to wait a bit for the toggle to complete, then save the state
                                setTimeout(() => {
                                    const newVisibilityState = {};
                                    chartContext.w.globals.seriesNames.forEach((name, idx) => {
                                        // Check if series is visible by checking if it's in the collapsed series array
                                        newVisibilityState[name] = !chartContext.w.globals.collapsedSeriesIndices.includes(idx);
                                    });
                                    // Save the new state
                                    saveSeriesVisibility(newVisibilityState);
                                    
                                    // Reset the flag after a short delay
                                    setTimeout(() => {
                                        isUserInteraction.current = false;
                                    }, 200);
                                }, 50);
                            },
                            mounted: function(chartContext, config) {
                                // Apply saved visibility state when chart is mounted
                                const savedVisibility = loadSeriesVisibility();
                                if (Object.keys(savedVisibility).length > 0 && !visibilityApplied.current) {
                                    // Small delay to ensure chart is fully initialized
                                    setTimeout(() => {
                                        applySeriesVisibility(chartContext, savedVisibility);
                                        visibilityApplied.current = true;
                                    }, 200);
                                }
                            },
                            updated: function(chartContext, config) {
                                // Only apply saved visibility if it's not a user interaction
                                // and visibility hasn't been applied yet for this data load
                                if (!isUserInteraction.current && !visibilityApplied.current) {
                                    const savedVisibility = loadSeriesVisibility();
                                    if (Object.keys(savedVisibility).length > 0) {
                                        setTimeout(() => {
                                            applySeriesVisibility(chartContext, savedVisibility);
                                            visibilityApplied.current = true;
                                        }, 200);
                                    }
                                }
                            },
                            dataPointSelection: function(chartContext, config) {
                                // Reset visibility applied flag when data changes
                                visibilityApplied.current = false;
                            }
                        }
                    }
                }
            }));
        } catch (error) {
            console.error("Error fetching room status snapshots:", error);
        }
    };
    const handleDateChange = (event) => {
        const selectedDateValue = event.target.value;
        setSelectedDate(selectedDateValue);
        if (selectedDateValue) {
            fetchRoomStatusSnapshots(selectedDateValue);
            setDateStr(moment(new Date(selectedDateValue)).format('YYYY-MM-DD'));
        }
    };

    return (
        <div className="container-xxl card">
            <h3 className="text-center">Room Status History</h3>
            <div className="form-group my-3">
                <label htmlFor="snapshotSelector">Select Snapshot Date</label>
                <select
                    id="snapshotSelector"
                    className="form-select shadow"
                    value={selectedDate}
                    onChange={handleDateChange}
                >
                    <option value="">Select a date</option>
                    {snapshotDates?.map((dateItem, index) => {
                        const snapshotDate = dateItem?.snapshot_date || dateItem;
                        return (
                            <option
                                key={snapshotDate || index}
                                value={snapshotDate}
                            >
                                {moment(snapshotDate).format('YYYY-MM-DD')}
                            </option>
                        );
                    })}
                </select>
            </div>

            <div className="mt-4">
                <Chart
                    ref={chartRef}
                    options={chartData.options}
                    series={chartData.series}
                    type="line"
                    height="650"
                />
            </div>
        </div>
    );
};


export default RoomStatusSnapshots;